/**
 * command-ui's identity, read at RUNTIME first (command-on-lon1 design §1.4, D1; 25 Sep 2026).
 *
 * One published image must run on cloch AND production (Maria's promotion rule: cloch -> soak ->
 * production, same bytes). Vite compiles `import.meta.env.VITE_*` into the bundle at build time, so
 * a single image used to carry exactly one estate's Clerk key and origin.
 *
 * Now: Caddy renders `/config.js` from the container's environment on every request (the
 * `templates` directive in Caddyfile.docker), and index.html loads it BEFORE the app bundle, so
 * `window.__COMMAND_CONFIG__` exists even for module-load reads (clerk-oauth.ts).
 *
 * Fallback: when a runtime value is absent (cloch today, whose hand-kept compose passes identity as
 * build arguments), the build-time value is used, so cloch is unchanged. Production can never fall
 * back silently: its declaration sets COMMAND_UI_REQUIRE_RUNTIME_IDENTITY=1, and the container then
 * REFUSES to start unless the runtime identity is present and the key's Clerk host matches
 * (scripts/runtime-identity-start.sh).
 */
export type RuntimeKey =
  | "VITE_CLERK_PUBLISHABLE_KEY"
  | "VITE_COMMAND_PUBLIC_ORIGIN"
  | "VITE_PLATFORM_STAFF_ORG_ID"
  | "VITE_SYNAPSEDX_CONSOLE"
  | "VITE_COMMUNITY_GRAB"
  | "VITE_UNIFIED_CATALOG"
  | "VITE_UNIFIED_SHELL_ORG_IDS";

type RuntimeConfig = Partial<Record<RuntimeKey, unknown>>;

declare global {
  interface Window {
    __COMMAND_CONFIG__?: RuntimeConfig;
  }
}

// Static accesses on purpose: Vite replaces `import.meta.env.VITE_X` literally, and a dynamic
// `import.meta.env[name]` is not guaranteed to survive the build. Read at CALL time, as every call
// site did before this file existed, so tests that stub the environment still see their stub.
const buildTime = (): Record<RuntimeKey, unknown> => ({
  VITE_CLERK_PUBLISHABLE_KEY: import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
  VITE_COMMAND_PUBLIC_ORIGIN: import.meta.env.VITE_COMMAND_PUBLIC_ORIGIN,
  VITE_PLATFORM_STAFF_ORG_ID: import.meta.env.VITE_PLATFORM_STAFF_ORG_ID,
  VITE_SYNAPSEDX_CONSOLE: import.meta.env.VITE_SYNAPSEDX_CONSOLE,
  VITE_COMMUNITY_GRAB: import.meta.env.VITE_COMMUNITY_GRAB,
  VITE_UNIFIED_CATALOG: import.meta.env.VITE_UNIFIED_CATALOG,
  VITE_UNIFIED_SHELL_ORG_IDS: import.meta.env.VITE_UNIFIED_SHELL_ORG_IDS,
});

/** A runtime value that Caddy did not render (the dev server serves the raw template) is absent. */
function usable(value: unknown): value is string {
  return typeof value === "string" && value.trim() !== "" && !value.includes("{{");
}

/**
 * The value for `name`: the runtime value when present, otherwise the build-time value. Returns
 * `undefined` when neither is a non-empty string, so callers keep their existing "unset" handling.
 */
export function runtimeEnv(name: RuntimeKey): string | undefined {
  const runtime = typeof window === "undefined" ? undefined : window.__COMMAND_CONFIG__?.[name];
  if (usable(runtime)) {
    return runtime;
  }
  const build = buildTime()[name];
  return typeof build === "string" && build !== "" ? build : undefined;
}
